import React, { useState } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Paginate from '../../components/Paginate';
import {
  useGetStoreDepositsQuery,
  useDeleteStoreDepositMutation,
  useCreateStoreDepositMutation,
} from '../../slices/storeDepositsApiSlice';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
        


const StoreDepositListScreen = () => {
  const { pageNumber } = useParams() || 1;

  const { data, isLoading, error, refetch } = useGetStoreDepositsQuery({ pageNumber });
  const [deleteStoreDeposit, { isLoading: loadingDelete }] = useDeleteStoreDepositMutation();
  const [createStoreDeposit, { isLoading: loadingCreate }] = useCreateStoreDepositMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
   

  // Create a new storeDeposit
  const createStoreDepositHandler = async () => {
    if (window.confirm('Are you sure you want to create a new storeDeposit?')) {
      try {
        const newStoreDeposit = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New StoreDeposit',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createStoreDeposit(newStoreDeposit).unwrap();
        toast.success('New storeDeposit created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Delete an storeDeposit
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this storeDeposit?')) {
      try {
        await deleteStoreDeposit(id).unwrap();
        toast.success('StoreDeposit deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort storeDeposits
  const filterAndSortStoreDeposits = () => {
    let filteredStoreDeposits = [...(data?.storeDeposits || [])];

    // Filter by text input
    if (filter) {
      filteredStoreDeposits = filteredStoreDeposits.filter(
        (storeDeposit) =>
          storeDeposit.account?.toLowerCase().includes(filter.toLowerCase()) ||
          storeDeposit.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          storeDeposit.details?.toLowerCase().includes(filter.toLowerCase()) ||
          storeDeposit.category?.toLowerCase().includes(filter.toLowerCase()) ||
          storeDeposit.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          storeDeposit.notes?.toLowerCase().includes(filter.toLowerCase()) ||
          storeDeposit.username?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredStoreDeposits = filteredStoreDeposits.filter((storeDeposit) => {
        const storeDepositDate = new Date(storeDeposit.date);
        return storeDepositDate >= start && storeDepositDate <= end;
      });
    }

    // Sort storeDeposits
    if (sortField) {
      filteredStoreDeposits.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredStoreDeposits;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredStoreDeposits = data?.storeDeposits?.filter((storeDeposit) => {
      const storeDepositDate = new Date(storeDeposit.date);
      return storeDepositDate >= start && storeDepositDate <= end;
    }) || [];

    return filteredStoreDeposits.reduce((acc, storeDeposit) => acc + storeDeposit.amount, 0);
  };

  // Calculate total for all storeDeposits
  const totalAmount = data?.storeDeposits?.reduce((acc, storeDeposit) => acc + storeDeposit.amount, 0) || 0;
   
  const downloadStoreDepositImages = async (storeDeposit) => {
          const zip = new JSZip();
          const folder = zip.folder(`StoreDeposit_${storeDeposit._id}`);
      
          const imagePromises = storeDeposit.images.map(async (imagePath, index) => {
            try {
              const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
              const blob = await response.blob();
              const urlParts = imagePath.split('/');
              const filename = urlParts[urlParts.length - 1]; // Use original filename
      
              folder.file(filename, blob);
            } catch (error) {
              console.error(`Failed to fetch ${imagePath}`, error);
            }
          });
      
          await Promise.all(imagePromises);
          zip.generateAsync({ type: 'blob' }).then((zipFile) => {
            saveAs(zipFile, `StoreDeposit_${storeDeposit._id}_Images.zip`);
          });
        };
      
        const downloadAllStoreDepositImages = async () => {
  if (!startDate || !endDate) {
    toast.error("Please select a start and end date first");
    return;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Filter storeDeposits by date range
  const filteredStoreDeposits =
    data?.storeDeposits?.filter((storeDeposit) => {
      const storeDepositDate = new Date(storeDeposit.date);
      return storeDepositDate >= start && storeDepositDate <= end;
    }) || [];

  // Count images first
  const allImages = filteredStoreDeposits.flatMap((d) => d.images || []);

  if (allImages.length === 0) {
    toast.warn("No storeDeposit images found in the selected date range");
    return;
  }

  setIsDownloading(true);
  setDownloadProgress(0);

  const zip = new JSZip();
  const folder = zip.folder("All_StoreDeposits_Images");

  let processed = 0;

  const imagePromises = filteredStoreDeposits.flatMap((storeDeposit) =>
    (storeDeposit.images || []).map(async (imagePath) => {
      try {
        const normalized = imagePath.replace(/\\/g, "/");
        const response = await fetch(normalized);
        const blob = await response.blob();

        const filename = normalized.split("/").pop();
        folder.file(filename, blob);
      } catch (error) {
        console.error(`Failed to fetch ${imagePath}`, error);
      }

      processed += 1;
      setDownloadProgress(Math.round((processed / allImages.length) * 100));
    })
  );

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, "All_StoreDeposits_Images.zip");

    toast.success(
      `Download complete. ${allImages.length} image${allImages.length !== 1 ? "s" : ""} included.`
    );

    setIsDownloading(false);
    setDownloadProgress(0);
  });
        };
        const exportToExcel = () => {
          if (!data || !data.storeDeposits || data.storeDeposits.length === 0) {
            toast.error("No storeDeposits available to export!");
            return;
          }
        
          const storeDepositsData = data.storeDeposits.map((storeDeposit) => ({
            Date: new Date(storeDeposit.date).toLocaleDateString(),
            Account: storeDeposit.account,
            AccountName: storeDeposit.accountName,
            SubAccount: storeDeposit.subAccount,
            Details: storeDeposit.details,
            Amount: `${storeDeposit.amount.toFixed(2)}`,
            Category: storeDeposit.category,
            Notes: storeDeposit.notes,
            Status: storeDeposit.status,
          }));
        
          const worksheet = XLSX.utils.json_to_sheet(storeDepositsData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'StoreDeposits');
        
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
        
          saveAs(file, 'StoreDeposits.xlsx');
        };
    
  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Bank Data</h1>
        </Col>
        <Col className="text-end">
          
          <Button
  className="my-3 mx-2"
  onClick={downloadAllStoreDepositImages}
  disabled={isDownloading}
>
  {isDownloading ? (
    <>
      <span className="spinner-border spinner-border-sm me-2"></span>
      Zipping...
    </>
  ) : (
    <>
      <FaDownload /> Dl Images
    </>
  )}
</Button>

{isDownloading && (
  <div className="my-2" style={{ width: "250px" }}>
    <div className="progress" style={{ height: "8px" }}>
      <div
        className="progress-bar progress-bar-striped progress-bar-animated"
        role="progressbar"
        style={{ width: `${downloadProgress}%` }}
      ></div>
    </div>
    <small>{downloadProgress}%</small>
  </div>
)}

          <Button className="my-3 mx-2" onClick={exportToExcel}>
          <FaDownload /> Exp to Exl
          </Button>
          <Button onClick={createStoreDepositHandler} className="my-3" >
            <FaPlus /> Add Data
          </Button>
          <Link to='/create/storeDeposit' className='btn btn-light my-3'>
            Add Data
          </Link>
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: D{totalAmount.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): D{calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.storeDeposits?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Account</th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                  Account Name {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  Notes {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th>Images</th>
                <th>Actions</th>
                <th>Dld</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortStoreDeposits().map((storeDeposit) => (
                <tr key={storeDeposit._id}>
                  <td>{new Date(storeDeposit.date).toLocaleDateString()}</td>
                  <td>{storeDeposit.account}</td>
                  <td>{storeDeposit.accountName}</td>
                  <td>{storeDeposit.details}</td>
                  <td>
                    {storeDeposit.amount >= 0
                      ? `${storeDeposit.amount.toFixed(2)}`
                      : `-${Math.abs(storeDeposit.amount).toFixed(2)}`}
                  </td>
                  <td>{storeDeposit.category}</td>
                  <td>{storeDeposit.notes}</td>
                  
                  <td className="thumbnails">
                    {storeDeposit.images?.length > 0 && (
                      <img
                        src={storeDeposit.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '10px',
                          height: '10px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>



                  <td>
                    <Button
                      as={Link}
                      to={`/admin/storeDeposit/${storeDeposit._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Link
                       className="btn btn-primary btn-sm m-2"
                      to={`/storeDeposit/${storeDeposit._id}`}
                        role="button"
                    >
                    Details
                   </Link>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(storeDeposit._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                  <td>
                    <Button variant="info" className="btn-sm" onClick={() => downloadStoreDepositImages(storeDeposit)}>
                    <FaDownload /> Dld Images
                     </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="storeDepositlist" />
        </>
      ) : (
        <Message variant="info">No storeDeposits found</Message>
      )}
    </>
  );
};

export default StoreDepositListScreen;